import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  detectPlatform,
  dismissInstallTrigger,
  isTriggerDismissed,
  shouldShowInstallNudge,
  recordInstallTrigger,
} from '@/lib/deviceDetect';
import { track } from '@/lib/analytics';
import { InstallWalkthrough } from './InstallWalkthrough';

interface InstallNudgeProps {
  triggerId: string;
  emoji: string;
  title: string;
  subtitle: string;
  ctaText: string;
  onDismiss?: () => void;
  onInstall?: () => void;
  variant?: 'bottom_sheet' | 'banner' | 'card';
}

function useAndroidInstallPrompt(): [any] {
  const [prompt, setPrompt] = useState<any>(null);

  useEffect(() => {
    const handler = (e: any) => {
      e.preventDefault();
      setPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  return [prompt];
}

export function InstallNudge({
  triggerId,
  emoji,
  title,
  subtitle,
  ctaText,
  onDismiss,
  onInstall,
  variant = 'bottom_sheet',
}: InstallNudgeProps) {
  const platform = detectPlatform();
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [deferredPrompt] = useAndroidInstallPrompt();

  if (isTriggerDismissed(triggerId)) return null;
  if (!shouldShowInstallNudge()) return null;

  const handleInstallClick = async () => {
    recordInstallTrigger(triggerId);
    const device = platform.startsWith('android') ? 'android' : platform.startsWith('ios') ? 'ios' : 'desktop';

    if (deferredPrompt && platform.startsWith('android')) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        dismissInstallTrigger(triggerId);
        track('pwa_install_accepted', { trigger: triggerId, device });
        onInstall?.();
      } else {
        track('pwa_install_dismissed', { trigger: triggerId, device });
      }
      return;
    }

    track('pwa_install_accepted', { trigger: triggerId, device });
    setShowWalkthrough(true);
  };

  const handleDismiss = () => {
    dismissInstallTrigger(triggerId);
    track('pwa_install_dismissed', { trigger: triggerId });
    onDismiss?.();
  };

  if (showWalkthrough) {
    return (
      <InstallWalkthrough
        onClose={() => {
          setShowWalkthrough(false);
          handleDismiss();
        }}
        onDone={() => {
          setShowWalkthrough(false);
          dismissInstallTrigger(triggerId);
          onInstall?.();
        }}
      />
    );
  }

  if (variant === 'banner') {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 8000,
          padding: '12px 16px',
          paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
          background: 'rgba(10,10,10,0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderTop: '1px solid rgba(225,29,72,0.25)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <img
          src="/icons/icon-192x192.png"
          alt="Papacito"
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            flexShrink: 0,
            border: '1px solid rgba(225,29,72,0.3)',
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14,
            fontWeight: 700,
            color: '#fff',
            marginBottom: 2,
          }}>
            Add Papacito to your home screen
          </div>
          <div style={{
            fontSize: 12,
            color: 'rgba(255,255,255,0.45)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            Get match notifications instantly
          </div>
        </div>
        <button
          onClick={handleInstallClick}
          style={{
            background: 'linear-gradient(135deg, #e11d48, #9333ea)',
            color: '#fff',
            fontSize: 13,
            fontWeight: 700,
            padding: '10px 16px',
            borderRadius: 50,
            border: 'none',
            cursor: 'pointer',
            flexShrink: 0,
            boxShadow: '0 4px 16px rgba(225,29,72,0.4)',
            whiteSpace: 'nowrap',
          }}
        >
          Install
        </button>
        <button
          onClick={handleDismiss}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'rgba(255,255,255,0.3)',
            fontSize: 20,
            cursor: 'pointer',
            padding: '4px',
            flexShrink: 0,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>
    );
  }

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 28 }}
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 500,
            background: 'linear-gradient(180deg, #1a0a2e, #0d0018)',
            borderRadius: '20px 20px 0 0',
            padding: '20px 24px 44px',
            border: '1px solid rgba(255,255,255,0.1)',
            borderBottom: 'none',
          }}
        >
          <div
            style={{
              width: 36,
              height: 4,
              borderRadius: 2,
              background: 'rgba(255,255,255,0.15)',
              margin: '0 auto 20px',
            }}
          />

          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 52, marginBottom: 16 }}>{emoji}</div>
            <h3
              style={{
                color: '#fff',
                fontSize: 22,
                fontWeight: 800,
                margin: '0 0 10px',
                lineHeight: 1.2,
              }}
            >
              {title}
            </h3>
            <p
              style={{
                color: 'rgba(255,255,255,0.55)',
                fontSize: 15,
                margin: '0 0 28px',
                lineHeight: 1.6,
              }}
            >
              {subtitle}
            </p>
            <button
              onClick={handleInstallClick}
              style={{
                width: '100%',
                background: '#e11d48',
                color: '#fff',
                border: 'none',
                borderRadius: 14,
                padding: '16px 0',
                fontSize: 17,
                fontWeight: 700,
                cursor: 'pointer',
                marginBottom: 12,
                boxShadow: '0 4px 20px rgba(225,29,72,0.4)',
              }}
            >
              {ctaText}
            </button>
            <button
              onClick={handleDismiss}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'rgba(255,255,255,0.3)',
                fontSize: 14,
                cursor: 'pointer',
                padding: 8,
              }}
            >
              Not now
            </button>
          </div>
        </motion.div>
      </AnimatePresence>

      <div
        onClick={handleDismiss}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.6)',
          zIndex: 499,
        }}
      />
    </>
  );
}
